'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { useRef } from 'react';
import { pushPromotionEvent } from '@/features/gtm/gtmUtils';

// Define the type for the items it will receive
interface PromoItem {
  id?: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  link?: string;
}

interface PromoBannerSliderProps {
  items: PromoItem[];
}

export const PromoBannerSlider: React.FC<PromoBannerSliderProps> = ({
  items,
}) => {
  const viewedPromotionIds = useRef(new Set<string>());

  const trackPromotionView = (item: PromoItem, index: number) => {
    const id = item.id ?? `homepage_promo_${index + 1}`;
    if (viewedPromotionIds.current.has(id)) return;

    viewedPromotionIds.current.add(id);
    pushPromotionEvent('view_promotion', {
      id,
      name: item.title,
      creativeName: item.title,
      creativeSlot: `homepage_promo_${index + 1}`,
    });
  };

  const trackVisiblePromotions = (swiper: any) => {
    const slidesPerView = Number(swiper.params.slidesPerView) || 1;
    const firstVisibleIndex = Number(swiper.realIndex) || 0;

    for (let offset = 0; offset < slidesPerView; offset += 1) {
      const index = (firstVisibleIndex + offset) % items.length;
      const item = items[index];
      if (item) trackPromotionView(item, index);
    }
  };

  const handlePromotionSelect = (item: PromoItem, index: number) => {
    pushPromotionEvent('select_promotion', {
      id: item.id ?? `homepage_promo_${index + 1}`,
      name: item.title,
      creativeName: item.title,
      creativeSlot: `homepage_promo_${index + 1}`,
    });
  };

  return (
    <Swiper
      modules={[Autoplay]}
      spaceBetween={30}
      slidesPerView={3} // Start with the desktop view
      autoplay={{ delay: 3000, disableOnInteraction: false }}
      loop={true}
      onSwiper={trackVisiblePromotions}
      onSlideChange={trackVisiblePromotions}
      className="promo-banner-swiper"
      breakpoints={{
        0: { slidesPerView: 1 },
        768: { slidesPerView: 2 },
        1200: { slidesPerView: 3 },
      }}
    >
      {items.map((item, index) => (
        <SwiperSlide key={index}>
          <div className="promo-banner__item d-flex justify-content-center align-items-center">
            <div className="promo-banner__icon me-3">{item.icon}</div>
            {item?.link ? (
              <a
                target="_blank"
                href={`${item?.link}`}
                onClick={() => handlePromotionSelect(item, index)}
              >
                <div className="promo-banner__content">
                  <h6 className="promo-banner__title fw-medium mb-1">
                    {item.title}
                  </h6>
                  <p className="promo-banner__subtitle m-0 fs-small">
                    {item.subtitle}
                  </p>
                </div>
              </a>
            ) : (
              <div className="promo-banner__content">
                <h6 className="promo-banner__title fw-medium mb-1">
                  {item.title}
                </h6>
                <p className="promo-banner__subtitle m-0 fs-small">
                  {item.subtitle}
                </p>
              </div>
            )}
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};
